/**
 * Intent Registry
 *
 * Intent is a derived property — never stored in the database.
 * A blueprint's intent is determined by its actions:
 *   - 'approval' if any action is non-terminal (has work to do)
 *   - 'insight' if all actions are terminal (just needs acknowledgment)
 *
 * Use deriveIntent() for resolved Action[] or deriveIntentFromBindings()
 * for raw ActionBinding[] (looks up action types from the in-memory cache).
 */

import { getActionTypeEntry } from './action-types';

export interface IntentEntry {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly badgeTone: 'warning' | 'info' | 'neutral';
}

export const INTENT_REGISTRY = [
  {
    id: 'approval',
    label: 'Approval',
    description: 'Items requiring user decisions — actions are configured per blueprint',
    badgeTone: 'warning',
  },
  {
    id: 'insight',
    label: 'Insight',
    description: 'Informational items — read-only, no resolution-requiring actions',
    badgeTone: 'neutral',
  },
  {
    id: 'signal',
    label: 'Signal',
    description: 'Auto-archived intelligence — no user action or acknowledgment needed',
    badgeTone: 'info',
  },
] as const satisfies readonly IntentEntry[];

export type Intent = (typeof INTENT_REGISTRY)[number]['id'];

export function isIntent(value: string): value is Intent {
  return INTENT_REGISTRY.some((e) => e.id === value);
}

export function getIntentEntry(id: Intent | (string & {})): IntentEntry | undefined {
  return (INTENT_REGISTRY as readonly IntentEntry[]).find((e) => e.id === id);
}

/** Map intent to its category label (plural form used as sidebar/group key). */
const INTENT_TO_CATEGORY: Record<Intent, string> = {
  approval: 'approvals',
  insight: 'insights',
  signal: 'signals',
};

export function intentToCategoryLabel(intent: Intent): string {
  return INTENT_TO_CATEGORY[intent];
}

/**
 * Derive intent from resolved actions (Action[] with isTerminal already set).
 * Returns 'approval' if any action is non-terminal, 'insight' otherwise.
 */
export function deriveIntent(
  actions: ReadonlyArray<{ isTerminal?: boolean; handlerConfig?: unknown }>,
  autoArchive?: boolean,
): Intent {
  if (autoArchive) return 'signal';
  // LINK actions are excluded — see isLinkBinding. A deep-link button navigates and
  // completes no work, so it must not make a read-only insight look like it needs
  // approval. This is the variant blueprint-cache.ts uses (on resolved actions), so
  // it is the one that decides the sidebar category the user actually sees.
  return actions.filter((a) => !isLinkBinding(a)).some((a) => !a.isTerminal)
    ? 'approval'
    : 'insight';
}

function hasActionTypeSlug(x: unknown): x is { actionTypeSlug: string } {
  return (
    typeof x === 'object' &&
    x !== null &&
    'actionTypeSlug' in x &&
    typeof x['actionTypeSlug'] === 'string'
  );
}

/**
 * Derive intent from raw action bindings (as stored in the blueprint JSON column).
 * Looks up each binding's action type from the in-memory cache to check isTerminal.
 * Returns 'insight' if the cache is cold or no bindings are present.
 */
export function deriveIntentFromBindings(bindings: unknown[], autoArchive?: boolean): Intent {
  if (autoArchive) return 'signal';
  if (!Array.isArray(bindings) || bindings.length === 0) return 'insight';
  for (const b of bindings) {
    // A LINK action navigates: it dispatches no execution, records no
    // ActionExecution and can never complete work, so it must not make an item
    // look like it needs approval. Without this, adding a "View signal" deep link
    // to a read-only insight blueprint silently moved every one of its artifacts
    // from Insights into Approvals — the badge said Insight, the sidebar said
    // Approvals. Checked before the registry lookup because the registry's
    // `open_link` row is (correctly) non-terminal: nothing is concluded by
    // following a link.
    if (isLinkBinding(b)) continue;
    if (hasActionTypeSlug(b)) {
      const entry = getActionTypeEntry(b.actionTypeSlug);
      if (entry && !entry.isTerminal) return 'approval';
    }
  }
  return 'insight';
}

/**
 * A binding is link-style when it carries a `handlerConfig.url` — the same signal
 * `view-item-builders.ts` uses to render an anchor instead of a dispatch button.
 */
function isLinkBinding(b: unknown): boolean {
  if (typeof b !== 'object' || b === null) return false;
  const cfg = (b as { handlerConfig?: unknown }).handlerConfig;
  if (typeof cfg !== 'object' || cfg === null) return false;
  const url = (cfg as { url?: unknown }).url;
  return typeof url === 'string' && url !== '';
}
